import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
import { formatDate } from "../utils/utils";
/* global gdAlertify */

const PatronModal = ({ isOpen, onClose, patron, onSave }) => {
    const [formData, setFormData] = useState({
        id: "",
        titulo: "",
        descripcion: "",
        cantidad_auxiliares: 1,
        tipo: "fixed",
        configuracion: {
            period: "day",
            phases: [],
            horaInicio: "",
            horaFin: "",
            serviceDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
        }
    });

    const daysMap = [
        { value: 'Mon', label: 'Lu' },
        { value: 'Tue', label: 'Ma' },
        { value: 'Wed', label: 'Mi' },
        { value: 'Thu', label: 'Ju' },
        { value: 'Fri', label: 'Vi' },
        { value: 'Sat', label: 'Sa' },
        { value: 'Sun', label: 'Do' }
    ];

    // Detecta un slot "mixto" guardado como todos sus días en 'libre' (así
    // se codifica "Libre" para no inventar un período nuevo a nivel de BD).
    const esFaseLibre = (p) => p.period === 'mixto' && (p.dailyPeriods || []).length > 0 && (p.dailyPeriods || []).every(d => d === 'libre');

    useEffect(() => {
        if (patron) {
            const config = typeof patron.configuracion === 'string'
                ? JSON.parse(patron.configuracion)
                : patron.configuracion || { period: "day", phases: [], horaInicio: "", horaFin: "", serviceDays: [] };
            setFormData({
                id: patron.id,
                titulo: patron.titulo || "",
                descripcion: patron.descripcion || "",
                cantidad_auxiliares: patron.cantidad_auxiliares || 1,
                tipo: patron.tipo || "fixed",
                configuracion: {
                    ...config,
                    phases: (config.phases || []).map(p => esFaseLibre(p) ? { ...p, period: 'libre' } : p),
                }
            });
        } else {
            setFormData({
                id: "",
                titulo: "",
                descripcion: "",
                cantidad_auxiliares: 1,
                tipo: "fixed",
                configuracion: {
                    period: "day",
                    phases: [],
                    horaInicio: "",
                    horaFin: "",
                    serviceDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
                }
            });
        }
    }, [patron, isOpen]);

    if (!isOpen) return null;

    const handleConfigChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            configuracion: {
                ...prev.configuracion,
                [field]: value
            }
        }));
    };

    const toggleServiceDay = (day) => {
        const currentDays = formData.configuracion.serviceDays || [];
        const newDays = currentDays.includes(day)
            ? currentDays.filter(d => d !== day)
            : [...currentDays, day];
        handleConfigChange('serviceDays', newDays);
    };

    const addPhase = () => {
        const newPhases = [...(formData.configuracion.phases || []), { period: 'day', days: '', start: '', end: '', status: 'libre', dailyPeriods: [] }];
        handleConfigChange('phases', newPhases);
    };

    const removePhase = (index) => {
        const newPhases = formData.configuracion.phases.filter((_, i) => i !== index);
        handleConfigChange('phases', newPhases);
    };

    const handlePhaseChange = (index, field, value) => {
        const newPhases = formData.configuracion.phases.map((p, i) => {
            if (i === index) {
                const updatedRow = { ...p, [field]: value };
                if (field === 'days' && updatedRow.period === 'mixto') {
                    const numDays = parseInt(value) || 0;
                    updatedRow.dailyPeriods = Array.from({ length: numDays }, (_, k) => p.dailyPeriods?.[k] || 'full');
                } else if (field === 'period' && value === 'mixto') {
                    const numDays = parseInt(p.days) || 0;
                    updatedRow.dailyPeriods = Array.from({ length: numDays }, (_, k) => p.dailyPeriods?.[k] || 'full');
                }
                return updatedRow;
            }
            return p;
        });
        handleConfigChange('phases', newPhases);
    };

    const handleDailyPeriodChange = (rowIndex, dayIndex, value) => {
        const newPhases = formData.configuracion.phases.map((p, i) => {
            if (i === rowIndex) {
                const newDailyPeriods = [...(p.dailyPeriods || [])];
                newDailyPeriods[dayIndex] = value;
                return { ...p, dailyPeriods: newDailyPeriods };
            }
            return p;
        });
        handleConfigChange('phases', newPhases);
    };

    // "Libre" se guarda como un slot 'mixto' con todos sus días en 'libre'
    // (mismo formato que ya entiende el backend para un tramo de descanso),
    // para no tener que enseñarle a todo el sistema un período nuevo.
    const phasesParaGuardar = (phases) => (phases || []).map(p => {
        if (p.period === 'libre') {
            return {
                period: 'mixto',
                days: p.days,
                start: '',
                end: '',
                status: p.status || 'libre',
                dailyPeriods: Array.from({ length: parseInt(p.days) || 0 }, () => 'libre'),
            };
        }
        return p;
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.titulo) {
            gdAlertify.notiError("El título es obligatorio");
            return;
        }
        onSave({
            ...formData,
            configuracion: {
                ...formData.configuracion,
                phases: phasesParaGuardar(formData.configuracion.phases),
            },
        });
    };

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1065 }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title font-weight-bold">{formData.id ? 'Editar Patrón' : 'Nuevo Patrón de Rotación'}</h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <div className="row mb-3">
                                <div className="col-md-9">
                                    <label className="form-label font-weight-bold">Título del Patrón (Template)</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={formData.titulo}
                                        onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                                        placeholder="Ej: Rotativo 2x2, Fijo Lunes a Viernes..."
                                        required
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label font-weight-bold">Cant. Auxiliares</label>
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        value={formData.cantidad_auxiliares}
                                        onChange={(e) => setFormData({...formData, cantidad_auxiliares: e.target.value})}
                                        min="1"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <label className="form-label font-weight-bold">Descripción</label>
                                    <textarea 
                                        className="form-control" 
                                        value={formData.descripcion}
                                        onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                                        rows="2"
                                    />
                                </div>
                            </div>

                            <div className="p-3 border rounded bg-light mb-3">
                                <h6 className="font-weight-bold mb-3">Días de Servicio (Referencial)</h6>
                                <div className="row g-2">
                                    {daysMap.map(day => (
                                        <div key={day.value} className="col text-center">
                                            <label className="form-label d-block small mb-1">{day.label}</label>
                                            <input 
                                                type="checkbox" 
                                                className="form-check-input"
                                                checked={formData.configuracion.serviceDays?.includes(day.value)}
                                                onChange={() => toggleServiceDay(day.value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="row mb-3">
                                <div className="col-md-12">
                                    <label className="form-label font-weight-bold">Tipo de Patrón</label>
                                    <div className="mb-3">
                                        <div className="form-check form-check-inline">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name="tipoPatron" 
                                                id="tipoFijo" 
                                                value="fixed" 
                                                checked={formData.tipo === 'fixed'}
                                                onChange={() => setFormData({...formData, tipo: 'fixed'})}
                                            />
                                            <label className="form-check-label" htmlFor="tipoFijo">Fijo</label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name="tipoPatron" 
                                                id="tipoRotativo" 
                                                value="rotative"
                                                checked={formData.tipo === 'rotative'}
                                                onChange={() => setFormData({...formData, tipo: 'rotative'})}
                                            />
                                            <label className="form-check-label" htmlFor="tipoRotativo">Rotativo</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {formData.tipo === 'fixed' ? (
                                <div className="p-3 border rounded bg-white">
                                    <h6 className="font-weight-bold mb-3">Configuración Patrón Fijo</h6>
                                    <div className="row">
                                        <div className="col-md-12 mb-3">
                                            <label className="form-label">Periodo</label>
                                            <select 
                                                className="form-select"
                                                value={formData.configuracion.period}
                                                onChange={(e) => handleConfigChange('period', e.target.value)}
                                            >
                                                <option value="day">Sólo Día</option>
                                                <option value="night">Sólo Noche</option>
                                                <option value="full">Día y Noche</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Hora Inicio</label>
                                            <input 
                                                type="time" 
                                                className="form-control" 
                                                value={formData.configuracion.horaInicio}
                                                onChange={(e) => handleConfigChange('horaInicio', e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Hora Fin</label>
                                            <input 
                                                type="time" 
                                                className="form-control" 
                                                value={formData.configuracion.horaFin}
                                                onChange={(e) => handleConfigChange('horaFin', e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-3 border rounded bg-white">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="font-weight-bold mb-0">Configuración Ciclos Rotativos</h6>
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={addPhase}>
                                            <i className="bi bi-plus-circle me-1"></i> Agregar Fase
                                        </button>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-borderless">
                                            <thead>
                                                <tr className="small text-muted">
                                                    <th style={{ width: '25%' }}>Tipo</th>
                                                    <th style={{ width: '15%' }}>Días</th>
                                                    <th style={{ width: '20%' }}>Inicio</th>
                                                    <th style={{ width: '20%' }}>Fin</th>
                                                    <th style={{ width: '20%' }}>Acción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.configuracion.phases?.map((phase, index) => (
                                                    <React.Fragment key={index}>
                                                    <tr className="border-bottom">
                                                        <td className="align-middle">
                                                            <select 
                                                                className="form-select form-select-sm"
                                                                value={phase.period}
                                                                onChange={(e) => handlePhaseChange(index, 'period', e.target.value)}
                                                            >
                                                                <option value="day">Día</option>
                                                                <option value="night">Noche</option>
                                                                <option value="full">Día y Noche</option>
                                                                <option value="mixto">Mixto</option>
                                                                <option value="libre">Libre / Descanso</option>
                                                            </select>
                                                        </td>
                                                        <td className="align-middle">
                                                            <input 
                                                                type="number" 
                                                                className="form-control form-control-sm" 
                                                                value={phase.days}
                                                                onChange={(e) => handlePhaseChange(index, 'days', e.target.value)}
                                                                min="1"
                                                            />
                                                        </td>
                                                        <td className="align-middle">
                                                            <input 
                                                                type="time" 
                                                                className="form-control form-control-sm" 
                                                                value={phase.start}
                                                                onChange={(e) => handlePhaseChange(index, 'start', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="align-middle">
                                                            <input 
                                                                type="time" 
                                                                className="form-control form-control-sm" 
                                                                value={phase.end}
                                                                onChange={(e) => handlePhaseChange(index, 'end', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removePhase(index)}>
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {phase.period === 'mixto' && (
                                                        <tr key={`mixto-${index}`}>
                                                            <td colSpan="5" className="p-0 border-0">
                                                                <div className="d-flex flex-wrap gap-2 align-items-center bg-light px-2 py-1 mx-1 mb-2 rounded" style={{ borderLeft: '3px solid #0d6efd' }}>
                                                                    <span className="small fw-bold text-primary me-2"><i className="bi bi-gear-fill me-1"></i>Mixto:</span>
                                                                    {Array.from({ length: parseInt(phase.days) || 0 }).map((_, dayIdx) => (
                                                                        <div key={dayIdx} className="d-flex align-items-center mb-1 mt-1">
                                                                            <span className="small text-muted me-1" style={{fontSize: '0.75rem'}}>D{dayIdx + 1}:</span>
                                                                            <select
                                                                                className="form-select form-select-sm border-primary"
                                                                                style={{ width: '100px', fontSize: '0.75rem', padding: '0.1rem 0.5rem' }}
                                                                                value={phase.dailyPeriods?.[dayIdx] || 'full'}
                                                                                onChange={e => handleDailyPeriodChange(index, dayIdx, e.target.value)}
                                                                            >
                                                                                <option value="full">Día/Noche</option>
                                                                                <option value="day">Día</option>
                                                                                <option value="night">Noche</option>
                                                                                <option value="libre">Libre</option>
                                                                            </select>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                                ))}
                                            </tbody>
                                        </table>
                                        {(!formData.configuracion.phases || formData.configuracion.phases.length === 0) && (
                                            <div className="text-center py-3 text-muted small">
                                                No hay fases definidas. Haga clic en "Agregar Fase".
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer bg-light">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
                            <button type="submit" className="btn btn-primary px-4">
                                <i className="bi bi-save me-1"></i> Guardar Patrón
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

const PatronesRotacion = ({ modal = false, onClose }) => {
    const [patrones, setPatrones] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPatron, setSelectedPatron] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchPatrones = useCallback(async () => {
        gdAlertify.showLoader();
        setLoading(true);
        try {
            const response = await axiosInstance.get("/patrones-rotacion/listar");
            if (!response.data.error) {
                setPatrones(response.data.data);
            }
            gdAlertify.hideLoader();
        } catch (error) {
            console.error("Error fetching patrones:", error);
            gdAlertify.hideLoader();
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPatrones();
    }, [fetchPatrones]);

    const handleSave = async (data) => {
        gdAlertify.showLoader();
        try {
            const response = await axiosInstance.post("/patrones-rotacion/guardar", data);
            if (!response.data.error) {
                gdAlertify.notiSuccess(response.data.message);
                setIsModalOpen(false);
                fetchPatrones();
            } else {
                gdAlertify.notiError(response.data.message);
            }
            gdAlertify.hideLoader();
        } catch (error) {
            console.error("Error saving patron:", error);
            gdAlertify.hideLoader();
            gdAlertify.notiError("Error al guardar el patrón");
        }
    };

    const handleDelete = (id) => {
        gdAlertify.confirm({
            title: "Eliminar Patrón",
            message: "¿Está seguro de que desea eliminar este patrón de rotación?",
            onConfirm: async () => {
                gdAlertify.showLoader();
                try {
                    const response = await axiosInstance.post(`/patrones-rotacion/eliminar/${id}`);
                    if (!response.data.error) {
                        gdAlertify.notiSuccess(response.data.message);
                        fetchPatrones();
                    } else {
                        gdAlertify.notiError(response.data.message);
                    }
                    gdAlertify.hideLoader();
                } catch (error) {
                    console.error("Error deleting patron:", error);
                    gdAlertify.hideLoader();
                    gdAlertify.notiError("Error al eliminar el patrón");
                }
            }
        });
    };

    const columns = [
        { name: "id", title: "ID", width: 80 },
        { name: "titulo", title: "Título (Cant. Aux)", width: 250, render: (row) => `${row.titulo} (${row.cantidad_auxiliares || 1} Aux)` },
        { name: "descripcion", title: "Descripción", width: 350 },
        { 
            name: "tipo", 
            title: "Tipo", 
            width: 120,
            render: (row) => row.tipo === 'fixed' ? 'Fijo' : 'Rotativo'
        },
        { 
            name: "fecha_creacion", 
            title: "Creado", 
            width: 180,
            render: (row) => formatDate(row.fecha_creacion)
        },
        {
            name: "id",
            title: "Acciones",
            width: 150,
            render: (row) => (
                <div className="btn-group">
                    <button 
                        className="btn btn-sm btn-outline-primary" 
                        onClick={() => {
                            setSelectedPatron(row);
                            setIsModalOpen(true);
                        }}
                    >
                        <i className="bi bi-pencil-square"></i> Editar
                    </button>
                    <button 
                        className="btn btn-sm btn-outline-danger" 
                        onClick={() => handleDelete(row.id)}
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                </div>
            )
        }
    ];

    const contenido = (
        <>
                <div className={`${modal ? "modal-header" : "card-header bg-white py-3 border-bottom"} d-flex justify-content-between align-items-center`}>
                    <h3 className="mb-0 font-weight-bold text-primary">Patrones de Rotación de Turnos</h3>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                setSelectedPatron(null);
                                setIsModalOpen(true);
                            }}
                        >
                            <i className="bi bi-plus-lg me-2"></i> Crear Nuevo Patrón
                        </button>
                        {modal && <button type="button" className="btn-close" onClick={onClose}></button>}
                    </div>
                </div>
                <div className={modal ? "modal-body overflow-auto" : "card-body"}>
                    <GdTable
                        columns={columns}
                        data={patrones}
                        defaultItemsPerPage={25}
                        itemsPerPageOptions={[10, 25, 50, 100]}
                        initialSort={[0, "desc"]}
                        loading={loading}
                        exportFilename="patrones-rotacion.xlsx"
                    />
                </div>
        </>
    );

    const editorPatron = (
            <PatronModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                patron={selectedPatron}
                onSave={handleSave}
            />
    );

    if (modal) {
        return (
            <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: "90vw", width: "90vw", maxHeight: "90vh", margin: "5vh auto" }}>
                    <div className="modal-content" style={{ maxHeight: "90vh" }}>
                        {contenido}
                    </div>
                </div>
                {editorPatron}
            </div>
        );
    }

    return (
        <div className="container-fluid mt-5 mb-5">
            <div className="card shadow-sm border-0">
                {contenido}
            </div>
            {editorPatron}
        </div>
    );
};

export default PatronesRotacion;
